/* ================================================= */
/* ASSISTANT CONTEXT COMPRESSION LAYER               */
/*                                                   */
/* Intermediate layer between pastoralState and      */
/* the future AI assistant.                          */
/*                                                   */
/* Transforms the canonical pastoral state into      */
/* a compressed, prioritized, token-efficient        */
/* packet ready for assistant consumption.           */
/*                                                   */
/* Rules:                                            */
/* - No React. No JSX. No Tailwind. No hooks.        */
/* - 100% deterministic. No side effects.            */
/* - No OpenAI. No fetches. No streaming.            */
/* - No semantic duplication across output fields.   */
/* - The assistant reads THIS — not pastoralState.   */
/* ================================================= */

import { URGENCY_LEVEL, CONTINUITY_STATE } from "./pastoral-state";
import { NARRATIVE_THEME }                  from "./pastoral-narrative";
import { EXECUTION_STATE }                  from "./pastoral-execution";

const CONTEXT_VERSION = "1.0";

/* ============================= */
/* COMPACT EXECUTION LABELS      */
/* Short string for context      */
/* ============================= */

const COMPACT_EXEC_LABEL = {
  [EXECUTION_STATE.NOT_STARTED]:       "Sin inicio",
  [EXECUTION_STATE.INITIATED]:         "Iniciado",
  [EXECUTION_STATE.IN_PROGRESS]:       "En curso",
  [EXECUTION_STATE.FOLLOWING_THROUGH]: "Seguimiento activo",
  [EXECUTION_STATE.SUSTAINED]:         "Sostenido",
  [EXECUTION_STATE.FRAGMENTED]:        "Irregular",
  [EXECUTION_STATE.RECOVERING]:        "Recuperando",
  [EXECUTION_STATE.STALLED]:           "Interrumpido",
  [EXECUTION_STATE.OVERDUE]:           "Sin visita reciente",
};

/* ============================= */
/* STATIC REASONING MAP          */
/* theme → continuity → context  */
/* Indexed two-level for clean   */
/* fallback to _default          */
/* ============================= */

const REASONING_MAP = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]: {
    _default: {
      whyNow:                 "La situación pastoral concentra múltiples señales críticas simultáneas.",
      whyImportant:           "La convergencia de factores de riesgo indica deterioro activo que puede agravarse sin intervención.",
      suggestedPastoralFocus: "Visitar esta semana con enfoque en oración personal y acompañamiento espiritual.",
    },
    [CONTINUITY_STATE.RECOVERING]: {
      whyNow:                 "Hay un contacto reciente, pero persisten señales de crisis espiritual y de oración.",
      whyImportant:           "La visita reciente no es suficiente para resolver la carga espiritual acumulada.",
      suggestedPastoralFocus: "Continuar el acompañamiento enfocándose en las oraciones urgentes activas.",
    },
    [CONTINUITY_STATE.NEGLECTED]: {
      whyNow:                 "La continuidad pastoral se perdió durante un período de alta presión espiritual.",
      whyImportant:           "La ausencia combinada con peticiones críticas indica una situación que requiere respuesta inmediata.",
      suggestedPastoralFocus: "Retomar contacto esta semana priorizando la situación espiritual y de oración.",
    },
  },

  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: {
    _default: {
      whyNow:                 "La continuidad del acompañamiento pastoral se ha interrumpido.",
      whyImportant:           "Sin seguimiento regular, el vínculo pastoral se debilita progresivamente.",
      suggestedPastoralFocus: "Retomar el ritmo de visitas para restablecer la continuidad.",
    },
    [CONTINUITY_STATE.RECOVERING]: {
      whyNow:                 "Se retomó el contacto recientemente, pero el patrón de ausencia prolongada aún es visible.",
      whyImportant:           "La recuperación es frágil — requiere consolidación para evitar otra interrupción.",
      suggestedPastoralFocus: "Mantener el ritmo de visitas con regularidad para estabilizar el vínculo.",
    },
    [CONTINUITY_STATE.ACTIVE]: {
      whyNow:                 "Hay contacto reciente, pero el historial revela una ruptura de continuidad previa.",
      whyImportant:           "El patrón histórico puede repetirse si no se consolida el ritmo actual.",
      suggestedPastoralFocus: "Establecer un ritmo consistente para evitar que el seguimiento vuelva a interrumpirse.",
    },
    [CONTINUITY_STATE.NEGLECTED]: {
      whyNow:                 "El seguimiento pastoral muestra un patrón extendido de ausencia.",
      whyImportant:           "Sin contacto sostenido, la relación pastoral pierde profundidad y efectividad.",
      suggestedPastoralFocus: "Iniciar un proceso de recuperación con visitas frecuentes en las próximas semanas.",
    },
  },

  [NARRATIVE_THEME.RECOVERY_MOMENTUM]: {
    _default: {
      whyNow:                 "La relación pastoral está en proceso de recuperación.",
      whyImportant:           "Este momento es clave — el impulso de recuperación puede consolidarse o perderse.",
      suggestedPastoralFocus: "Mantener el ritmo de visitas para transformar la recuperación en continuidad estable.",
    },
    [CONTINUITY_STATE.DECLINING]: {
      whyNow:                 "La recuperación pastoral muestra señales de debilitamiento.",
      whyImportant:           "El ciclo anterior de recuperación y abandono puede repetirse si no se actúa pronto.",
      suggestedPastoralFocus: "Retomar el contacto antes de que el seguimiento vuelva a enfriarse.",
    },
    [CONTINUITY_STATE.ACTIVE]: {
      whyNow:                 "El seguimiento se regularizó después de una etapa de ausencia.",
      whyImportant:           "Mantener el ritmo evita que la recuperación se convierta en un ciclo intermitente.",
      suggestedPastoralFocus: "Sostener la frecuencia de visitas durante las próximas semanas.",
    },
  },

  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]: {
    _default: {
      whyNow:                 "El estado espiritual requiere atención pastoral activa.",
      whyImportant:           "Un estado espiritual bajo prolongado puede derivar en alejamiento si no se acompaña.",
      suggestedPastoralFocus: "Profundizar el acompañamiento espiritual con enfoque en la restauración.",
    },
    [CONTINUITY_STATE.NEGLECTED]: {
      whyNow:                 "La ausencia pastoral coincide con un estado espiritual bajo.",
      whyImportant:           "La combinación de ausencia y vulnerabilidad espiritual amplifica el riesgo pastoral.",
      suggestedPastoralFocus: "Retomar el contacto con sensibilidad espiritual — el momento es delicado.",
    },
    [CONTINUITY_STATE.ACTIVE]: {
      whyNow:                 "Hay visitas recientes, pero el estado espiritual sigue siendo bajo.",
      whyImportant:           "La frecuencia de visitas es adecuada, pero el contenido del acompañamiento necesita profundizarse.",
      suggestedPastoralFocus: "Orientar las visitas hacia el acompañamiento espiritual en lugar del seguimiento rutinario.",
    },
  },

  [NARRATIVE_THEME.PRAYER_BURDEN]: {
    _default: {
      whyNow:                 "Hay oraciones urgentes activas que esperan seguimiento pastoral.",
      whyImportant:           "Las peticiones de oración sin seguimiento generan distancia espiritual.",
      suggestedPastoralFocus: "Dar seguimiento personal a las oraciones urgentes en la próxima visita.",
    },
    [CONTINUITY_STATE.NEGLECTED]: {
      whyNow:                 "Las oraciones urgentes coinciden con una interrupción del seguimiento pastoral.",
      whyImportant:           "La percepción de abandono en momentos de necesidad puede dañar el vínculo pastoral de forma duradera.",
      suggestedPastoralFocus: "Visitar pronto con enfoque específico en la situación de oración activa.",
    },
  },

  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]: {
    _default: {
      whyNow:                 "El seguimiento pastoral ha superado el umbral de atención.",
      whyImportant:           "Una visita en el próximo período evita que la situación escale a estado de descuido.",
      suggestedPastoralFocus: "Planificar la visita para esta o la próxima semana.",
    },
    [CONTINUITY_STATE.DECLINING]: {
      whyNow:                 "El ritmo de visitas está disminuyendo respecto al historial.",
      whyImportant:           "La reducción de frecuencia puede indicar un alejamiento gradual.",
      suggestedPastoralFocus: "Retomar el ritmo anterior de visitas para evitar que el seguimiento se enfríe.",
    },
    [CONTINUITY_STATE.UNKNOWN]: {
      whyNow:                 "No hay historial suficiente para evaluar la continuidad del acompañamiento.",
      whyImportant:           "Establecer un registro de visitas permite detectar cambios de ritmo a tiempo.",
      suggestedPastoralFocus: "Realizar y registrar la próxima visita para construir historial pastoral.",
    },
  },

  [NARRATIVE_THEME.GROWTH_WINDOW]: {
    _default: {
      whyNow:                 "El proceso bautismal está activo — hay una oportunidad de acompañamiento espiritual.",
      whyImportant:           "Las ventanas de crecimiento espiritual son momentos de alta receptividad que merecen prioridad.",
      suggestedPastoralFocus: "Acompañar activamente el proceso bautismal con visitas de seguimiento espiritual.",
    },
    [CONTINUITY_STATE.RECOVERING]: {
      whyNow:                 "El proceso bautismal avanza junto con una recuperación del ritmo de visitas.",
      whyImportant:           "El doble impulso de crecimiento espiritual y recuperación pastoral crea un momento especialmente favorable.",
      suggestedPastoralFocus: "Aprovechar el impulso actual para profundizar el acompañamiento bautismal.",
    },
  },

  [NARRATIVE_THEME.PASTORAL_OVERLOAD]: {
    _default: {
      whyNow:                 "La carga pastoral supera la capacidad operativa actual.",
      whyImportant:           "La sobrecarga sostenida reduce la calidad del seguimiento en todas las entidades.",
      suggestedPastoralFocus: "Revisar la distribución de responsabilidades y priorizar por urgencia.",
    },
  },

  [NARRATIVE_THEME.HEALTHY_STABILITY]: {
    _default: {
      whyNow:                 null,
      whyImportant:           null,
      suggestedPastoralFocus: "Mantener el ritmo pastoral actual.",
    },
  },
};

/* ============================= */
/* DISTRICT REASONING MAP        */
/* Indexed by continuityState    */
/* ============================= */

const DISTRICT_REASONING_MAP = {
  [CONTINUITY_STATE.NEGLECTED]: {
    dominantPressure: "continuity",
    districtRisk:     "concentrated",
    operationalNeed:  "grouped_followup",
  },
  [CONTINUITY_STATE.DECLINING]: {
    dominantPressure: "continuity",
    districtRisk:     "scattered",
    operationalNeed:  "targeted_visits",
  },
  [CONTINUITY_STATE.ACTIVE]: {
    dominantPressure: "maintenance",
    districtRisk:     "low",
    operationalNeed:  "sustain_rhythm",
  },
  [CONTINUITY_STATE.RECOVERING]: {
    dominantPressure: "consolidation",
    districtRisk:     "improving",
    operationalNeed:  "momentum_protection",
  },
  [CONTINUITY_STATE.UNKNOWN]: {
    dominantPressure: "data_insufficient",
    districtRisk:     "unknown",
    operationalNeed:  "assessment",
  },
};

/* ============================= */
/* 1. SIGNAL COMPRESSION         */
/* Top signals, deduplicated     */
/* by source, severity-ranked    */
/* ============================= */

function compressPastoralSignals(semanticSignals) {
  if (!semanticSignals || semanticSignals.length === 0) return [];

  const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3, positive: 4 };

  // Keep only the highest-severity signal per source
  const bySource = {};
  for (const signal of semanticSignals) {
    const existing = bySource[signal.source];
    if (
      !existing ||
      (SEVERITY_ORDER[signal.severity] ?? 5) < (SEVERITY_ORDER[existing.severity] ?? 5)
    ) {
      bySource[signal.source] = signal;
    }
  }

  return Object.values(bySource)
    .filter(s => s.severity !== "positive" && s.severity !== "low")
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 5) - (SEVERITY_ORDER[b.severity] ?? 5))
    .slice(0, 5)
    .map(s => s.type);
}

/* ============================= */
/* 2. REASONING CONTEXT          */
/* Static deterministic context  */
/* ============================= */

function buildReasoningContext(dominantTheme, continuityState) {
  const themeMap = REASONING_MAP[dominantTheme];
  if (!themeMap) return null;
  return themeMap[continuityState] ?? themeMap._default ?? null;
}

function buildDistrictReasoningContext(continuityState) {
  return DISTRICT_REASONING_MAP[continuityState] ?? DISTRICT_REASONING_MAP[CONTINUITY_STATE.UNKNOWN];
}

/* ============================= */
/* 3. RECOMMENDATIONS            */
/* Max 3. Deterministic.         */
/* Priority: continuity →        */
/* prayer → workflow → execution */
/* ============================= */

function buildEntityRecommendations(pastoralState) {
  const {
    urgency,
    continuityState,
    groupedContext,
    executionHealth,
    dominantTheme,
  } = pastoralState;

  const recs = [];
  const used = new Set();

  const add = (key, message, reason) => {
    if (!used.has(key)) {
      used.add(key);
      recs.push({ message, reason });
    }
  };

  // Immediate crisis — override everything
  if (urgency === URGENCY_LEVEL.IMMEDIATE) {
    add("visit", "Realizar una visita pastoral esta semana", "La situación requiere atención inmediata");
  }

  // Prayer urgency
  if (groupedContext.prayerPressure === "critical") {
    add("prayer", "Atender las oraciones urgentes personalmente", "Hay peticiones de oración críticas activas");
  }

  // Continuity collapse (only when not already covered by visit above)
  if (
    (groupedContext.continuityPressure === "critical" || groupedContext.continuityPressure === "medium") &&
    urgency !== URGENCY_LEVEL.IMMEDIATE
  ) {
    add("visit", "Retomar el ritmo de visitas esta semana", "La continuidad del acompañamiento se ha interrumpido");
  }

  // Prayer follow-through (deduplicated from critical)
  if (groupedContext.prayerPressure === "high" && !used.has("prayer")) {
    add("prayer", "Revisar y acompañar las oraciones activas", "Hay múltiples peticiones de oración que merecen seguimiento");
  }

  // Execution fragmentation
  if (
    executionHealth === EXECUTION_STATE.FRAGMENTED &&
    recs.length < 3 &&
    !used.has("cadence")
  ) {
    add("cadence", "Establecer un ritmo constante de visitas", "El historial muestra irregularidad — la consistencia es clave");
  }

  // Recovery consolidation
  if (continuityState === CONTINUITY_STATE.RECOVERING && recs.length < 3 && !used.has("visit")) {
    add("recovery", "Sostener el ritmo actual para consolidar la recuperación", "El seguimiento está retomándose — no perder el impulso");
  }

  // Baptism window
  if (dominantTheme === NARRATIVE_THEME.GROWTH_WINDOW && recs.length < 3) {
    add("baptism", "Acompañar el proceso bautismal en curso", "Hay una ventana de crecimiento espiritual activa");
  }

  return recs.slice(0, 3);
}

function buildDistrictRecommendations(districtPastoralState) {
  const {
    urgency,
    continuityState,
    groupedContext,
    hasOverload,
  } = districtPastoralState;

  const recs = [];
  const used = new Set();

  const add = (key, message, reason) => {
    if (!used.has(key)) {
      used.add(key);
      recs.push({ message, reason });
    }
  };

  if (urgency === URGENCY_LEVEL.IMMEDIATE) {
    add("crisis", "Priorizar iglesias con múltiples señales críticas", "El distrito enfrenta situaciones que requieren respuesta inmediata");
  }

  if (hasOverload && !used.has("load")) {
    add("load", "Revisar la distribución de responsabilidades pastorales", "La carga pastoral supera la capacidad operativa actual");
  }

  if (
    (groupedContext.continuityPressure === "critical" || groupedContext.continuityPressure === "medium") &&
    !used.has("continuity")
  ) {
    add("continuity", "Planificar visitas grupales por zona para las iglesias sin seguimiento reciente", "Varias iglesias muestran signos de interrupción en el seguimiento");
  }

  if (groupedContext.prayerPressure === "high" && !used.has("prayer")) {
    add("prayer", "Dar seguimiento a las oraciones urgentes del distrito", "Hay presión de oración concentrada en el distrito");
  }

  if (continuityState === CONTINUITY_STATE.RECOVERING && recs.length < 3) {
    add("recovery", "Mantener el ritmo actual para consolidar la recuperación distrital", "El distrito muestra señales de recuperación — proteger el impulso");
  }

  return recs.slice(0, 3);
}

/* ============================= */
/* 4. COMPACT STATE              */
/* Ultra-small for push/cache    */
/* ============================= */

function buildCompactState(pastoralState) {
  return {
    theme:       pastoralState.dominantTheme    ?? null,
    urgency:     pastoralState.urgency          ?? URGENCY_LEVEL.NONE,
    action:      pastoralState.primaryAction    ?? null,
    continuity:  pastoralState.continuityState  ?? CONTINUITY_STATE.UNKNOWN,
    execution:   pastoralState.executionHealth  ?? null,
    confidence:  pastoralState.confidence       ?? 0,
  };
}

/* ============================= */
/* 5. TOKEN ESTIMATE             */
/* ~4 chars per token (Spanish)  */
/* For future prompt budgeting   */
/* ============================= */

function estimateTokenWeight(context) {
  const approx = (val) => {
    if (!val) return 0;
    const str = typeof val === "string" ? val : JSON.stringify(val);
    return Math.ceil(str.length / 4);
  };

  const summaryTokens         = approx(context.summary);
  const reasoningTokens       = approx(context.reasoning);
  const signalsTokens         = approx(context.pastoralSignals);
  const recommendationTokens  = approx(context.recommendations);
  const totalTokens           = summaryTokens + reasoningTokens + signalsTokens + recommendationTokens;

  return {
    totalTokens,
    breakdown: {
      summary:         summaryTokens,
      reasoning:       reasoningTokens,
      signals:         signalsTokens,
      recommendations: recommendationTokens,
    },
  };
}

/* ============================= */
/* 6. PASTORAL PRESSURE          */
/* Single pressure label         */
/* ============================= */

function derivePastoralPressure(urgency, calmState) {
  if (calmState)                           return "calm";
  if (urgency === URGENCY_LEVEL.IMMEDIATE) return "critical";
  if (urgency === URGENCY_LEVEL.SOON)      return "high";
  if (urgency === URGENCY_LEVEL.MONITOR)   return "moderate";
  return "calm";
}

/* ============================= */
/* ENTITY ENTRY POINT            */
/* buildAssistantContext          */
/* ============================= */

/**
 * Compressed assistant-ready context from a canonical pastoral state.
 *
 * @param {object} pastoralState — output of buildPastoralState()
 * @param {object} [options] — reserved for future use
 * @returns {AssistantContextPacket}
 */
export function buildAssistantContext(pastoralState, options = {}) {
  if (!pastoralState) return null;

  const {
    entityType,
    entityId,
    dominantTheme,
    urgency,
    continuityState,
    narrativeSummary,
    assistantSummary,
    primaryAction,
    secondaryActions,
    executionHealth,
    cadence,
    groupedContext,
    semanticSignals,
    suppressions,
    confidence,
  } = pastoralState;

  const calmState      = pastoralState.suppressions?.narrativeStrip ?? false;
  const pastoralPressure = derivePastoralPressure(urgency, calmState);

  // Summary: use pre-computed assistantSummary from pastoral-state.
  // Fall back to narrativeSummary. Calm → brief stability note.
  const summary = calmState
    ? "El acompañamiento pastoral se mantiene estable."
    : assistantSummary ?? narrativeSummary ?? null;

  const reasoning          = buildReasoningContext(dominantTheme, continuityState);
  const pastoralSignals    = compressPastoralSignals(semanticSignals);
  const recommendations    = buildEntityRecommendations(pastoralState);
  const compactState       = buildCompactState(pastoralState);

  const executionContext = executionHealth
    ? {
        state:   executionHealth,
        cadence: cadence ?? null,
        label:   COMPACT_EXEC_LABEL[executionHealth] ?? null,
      }
    : null;

  const contextPayload = {
    version: CONTEXT_VERSION,

    entityType,
    entityId,

    summary,

    dominantTheme,
    urgency,
    continuityState,

    primaryAction,
    secondaryActions: (secondaryActions ?? []).slice(0, 2),

    reasoning,

    pastoralSignals,

    execution: executionContext,

    pastoralPressure,

    groupedContext,

    recommendations,

    suppressions: suppressions ?? null,

    confidence,

    compactState,
  };

  contextPayload.tokenEstimate = estimateTokenWeight(contextPayload);

  return contextPayload;
}

/* ============================= */
/* DISTRICT ENTRY POINT          */
/* buildDistrictAssistantContext  */
/* ============================= */

/**
 * Compressed assistant-ready context for the district.
 *
 * @param {object} districtPastoralState — output of buildDistrictPastoralState()
 * @param {object} [options] — reserved
 * @returns {DistrictAssistantContextPacket}
 */
export function buildDistrictAssistantContext(districtPastoralState, options = {}) {
  if (!districtPastoralState) return null;

  const {
    dominantTheme,
    urgency,
    continuityState,
    narrativeSummary,
    assistantSummary,
    primaryAction,
    executionSummary,
    loadLevel,
    hasOverload,
    groupedContext,
    semanticSignals,
    suppressions,
    confidence,
  } = districtPastoralState;

  const calmState        = districtPastoralState.suppressions?.alerts ?? false;
  const pastoralPressure = derivePastoralPressure(urgency, calmState);

  const summary = calmState
    ? "El distrito mantiene un ritmo pastoral estable."
    : assistantSummary ?? narrativeSummary ?? null;

  const reasoning       = buildDistrictReasoningContext(continuityState);
  const pastoralSignals = compressPastoralSignals(semanticSignals ?? []);
  const recommendations = buildDistrictRecommendations(districtPastoralState);

  const compactState = {
    theme:      dominantTheme   ?? null,
    urgency:    urgency         ?? URGENCY_LEVEL.NONE,
    load:       loadLevel       ?? null,
    continuity: continuityState ?? CONTINUITY_STATE.UNKNOWN,
    overloaded: hasOverload     ?? false,
    confidence: confidence      ?? 0,
  };

  const contextPayload = {
    version: CONTEXT_VERSION,

    entityType: "district",
    entityId:   null,

    summary,

    dominantTheme,
    urgency,
    continuityState,

    primaryAction,
    secondaryActions: [],

    reasoning,

    pastoralSignals,

    execution:        null,
    executionSummary: executionSummary ?? null,

    pastoralPressure,

    loadLevel,
    hasOverload: hasOverload ?? false,

    groupedContext,

    recommendations,

    suppressions: suppressions ?? null,

    confidence,

    compactState,
  };

  contextPayload.tokenEstimate = estimateTokenWeight(contextPayload);

  return contextPayload;
}
